import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { TokenManager, type TokenManagerState } from "./token-manager";

export interface EditorAuthContextValue extends TokenManagerState {
  getToken: () => Promise<string>;
  refresh: () => Promise<void>;
}

const EditorAuthContext = createContext<EditorAuthContextValue | null>(null);
// Separate context for the raw manager so the API client can share the very
// same instance (one token, one timer) without every consumer holding it.
const ManagerContext = createContext<TokenManager | null>(null);

export interface EditorAuthProviderProps {
  children: ReactNode;
  /** Injectable for tests and for wiring the API client. Omit in production. */
  manager?: TokenManager;
}

/**
 * Mount ONCE at the app root, above the router. Mounting twice creates two
 * TokenManagers, two refresh timers, and two competing message listeners.
 */
export function EditorAuthProvider({ children, manager: injected }: EditorAuthProviderProps) {
  const manager = useMemo(() => injected ?? new TokenManager(), [injected]);
  const [state, setState] = useState<TokenManagerState>(manager.getState());

  useEffect(() => {
    const unsubscribe = manager.subscribe(setState);
    manager.init().catch(() => {
      /* surfaced through state.status === "error" */
    });
    /**
     * Coming BACK is the moment that matters.
     *
     * The proactive timer covers an editor left open in a visible tab. It
     * cannot cover a sleeping machine or a hidden tab, where `setTimeout` is
     * suspended or throttled while real time keeps passing — so the refresh
     * that should have happened at 80% of the token's life silently does not,
     * and the merchant's next click 401s.
     *
     * Both events, because neither alone is enough: `visibilitychange` misses
     * an already-visible window that merely regained focus (alt-tab, or a
     * click back into the iframe from the dashboard around it), and `focus`
     * does not fire when a backgrounded tab is re-selected. The manager
     * itself decides whether a refresh is actually due, so firing this often
     * is free — it is a clock comparison, not a request.
     */
    const onWake = () => {
      if (document.visibilityState === "visible") manager.refreshIfStale();
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
      unsubscribe();
      manager.dispose();
    };
  }, [manager]);

  const value = useMemo<EditorAuthContextValue>(
    () => ({
      ...state,
      getToken: () => manager.getToken(),
      refresh: () => manager.refresh().then(() => undefined),
    }),
    [manager, state],
  );

  return (
    <ManagerContext.Provider value={manager}>
      <EditorAuthContext.Provider value={value}>{children}</EditorAuthContext.Provider>
    </ManagerContext.Provider>
  );
}

export function useEditorAuth(): EditorAuthContextValue {
  const ctx = useContext(EditorAuthContext);
  if (!ctx) throw new Error("useEditorAuth must be used within an EditorAuthProvider");
  return ctx;
}

export function useEditorAuthManager(): TokenManager {
  const ctx = useContext(ManagerContext);
  if (!ctx) throw new Error("useEditorAuthManager must be used within an EditorAuthProvider");
  return ctx;
}
